import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import * as emailjs from '@emailjs/browser';
import { Mail, Phone, MapPin, Clock, Send, Sparkles, Zap, ChevronDown } from 'lucide-react';

const countries = [
  { name: 'Afghanistan', code: 'AF', dialCode: '+93', flag: '🇦🇫' },
  { name: 'Albania', code: 'AL', dialCode: '+355', flag: '🇦🇱' },
  { name: 'Algeria', code: 'DZ', dialCode: '+213', flag: '🇩🇿' },
  { name: 'Argentina', code: 'AR', dialCode: '+54', flag: '🇦🇷' },
  { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺' },
  { name: 'Austria', code: 'AT', dialCode: '+43', flag: '🇦🇹' },
  { name: 'Bangladesh', code: 'BD', dialCode: '+880', flag: '🇧🇩' },
  { name: 'Belgium', code: 'BE', dialCode: '+32', flag: '🇧🇪' },
  { name: 'Brazil', code: 'BR', dialCode: '+55', flag: '🇧🇷' },
  { name: 'Canada', code: 'CA', dialCode: '+1', flag: '🇨🇦' },
  { name: 'China', code: 'CN', dialCode: '+86', flag: '🇨🇳' },
  { name: 'Colombia', code: 'CO', dialCode: '+57', flag: '🇨🇴' },
  { name: 'Denmark', code: 'DK', dialCode: '+45', flag: '🇩🇰' },
  { name: 'Egypt', code: 'EG', dialCode: '+20', flag: '🇪🇬' },
  { name: 'Finland', code: 'FI', dialCode: '+358', flag: '🇫🇮' },
  { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷' },
  { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪' },
  { name: 'Greece', code: 'GR', dialCode: '+30', flag: '🇬🇷' },
  { name: 'Hong Kong', code: 'HK', dialCode: '+852', flag: '🇭🇰' },
  { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳' },
  { name: 'Indonesia', code: 'ID', dialCode: '+62', flag: '🇮🇩' },
  { name: 'Iran', code: 'IR', dialCode: '+98', flag: '🇮🇷' },
  { name: 'Iraq', code: 'IQ', dialCode: '+964', flag: '🇮🇶' },
  { name: 'Ireland', code: 'IE', dialCode: '+353', flag: '🇮🇪' },
  { name: 'Israel', code: 'IL', dialCode: '+972', flag: '🇮🇱' },
  { name: 'Italy', code: 'IT', dialCode: '+39', flag: '🇮🇹' },
  { name: 'Japan', code: 'JP', dialCode: '+81', flag: '🇯🇵' },
  { name: 'Malaysia', code: 'MY', dialCode: '+60', flag: '🇲🇾' },
  { name: 'Mexico', code: 'MX', dialCode: '+52', flag: '🇲🇽' },
  { name: 'Nepal', code: 'NP', dialCode: '+977', flag: '🇳🇵' },
  { name: 'Netherlands', code: 'NL', dialCode: '+31', flag: '🇳🇱' },
  { name: 'New Zealand', code: 'NZ', dialCode: '+64', flag: '🇳🇿' },
  { name: 'Norway', code: 'NO', dialCode: '+47', flag: '🇳🇴' },
  { name: 'Pakistan', code: 'PK', dialCode: '+92', flag: '🇵🇰' },
  { name: 'Philippines', code: 'PH', dialCode: '+63', flag: '🇵🇭' },
  { name: 'Poland', code: 'PL', dialCode: '+48', flag: '🇵🇱' },
  { name: 'Portugal', code: 'PT', dialCode: '+351', flag: '🇵🇹' },
  { name: 'Qatar', code: 'QA', dialCode: '+974', flag: '🇶🇦' },
  { name: 'Romania', code: 'RO', dialCode: '+40', flag: '🇷🇴' },
  { name: 'Russia', code: 'RU', dialCode: '+7', flag: '🇷🇺' },
  { name: 'Saudi Arabia', code: 'SA', dialCode: '+966', flag: '🇸🇦' },
  { name: 'Singapore', code: 'SG', dialCode: '+65', flag: '🇸🇬' },
  { name: 'South Africa', code: 'ZA', dialCode: '+27', flag: '🇿🇦' },
  { name: 'South Korea', code: 'KR', dialCode: '+82', flag: '🇰🇷' },
  { name: 'Spain', code: 'ES', dialCode: '+34', flag: '🇪🇸' },
  { name: 'Sweden', code: 'SE', dialCode: '+46', flag: '🇸🇪' },
  { name: 'Switzerland', code: 'CH', dialCode: '+41', flag: '🇨🇭' },
  { name: 'Thailand', code: 'TH', dialCode: '+66', flag: '🇹🇭' },
  { name: 'Turkey', code: 'TR', dialCode: '+90', flag: '🇹🇷' },
  { name: 'UAE', code: 'AE', dialCode: '+971', flag: '🇦🇪' },
  { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧' },
  { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸' },
  { name: 'Vietnam', code: 'VN', dialCode: '+84', flag: '🇻🇳' }
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone: {
      country: countries.find(c => c.code === 'NP') || countries[0],
      number: ''
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(countries);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    emailjs.init("vHAZnJw1QbFB_PSzT");
  }, []);

  useEffect(() => {
    setFilteredCountries(
      countries.filter(country =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.dialCode.includes(searchQuery)
      )
    );
  }, [searchQuery]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitStatus('Sending...');

    const templateParams = {
      to_name: 'Gortherm Energy',
      from_name: formData.name,
      from_email: formData.email,
      subject: formData.subject,
      phone: `${formData.phone.country.dialCode} ${formData.phone.number}`,
      message: formData.message
    };

    emailjs.send('service_pbms109', 'template_272usi6', templateParams)
      .then((response) => {
        console.log('Email sent successfully!', response.status, response.text);
        setSubmitStatus('Message sent successfully!');
        
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          phone: {
            country: countries.find(c => c.code === 'NP') || countries[0],
            number: ''
          }
        });
        
        setTimeout(() => setSubmitStatus(''), 5000);
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        setSubmitStatus('Failed to send message. Please try again.');
        setTimeout(() => setSubmitStatus(''), 5000);
      });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    setFormData({
      ...formData,
      phone: { ...formData.phone, number: cleaned }
    });
  };

  // Structured data for contact page
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com",
      "logo": "https://www.gorthermenergy.com/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+977-9802781400",
        "contactType": "Customer Service",
        "email": "info@gorthermenergy.com",
        "areaServed": "NP",
        "availableLanguage": ["English", "Nepali"],
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Yashoda Bhawan, H08",
        "addressLocality": "Biratnagar",
        "postalCode": "56613",
        "addressCountry": "NP"
      }
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": "https://www.gorthermenergy.com/contact"
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What services does Gortherm Energy provide?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We specialize in industrial boiler sales and service, steam accessories supply, process engineering, and water treatment solutions for industries across Nepal."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can I expect a response to my inquiry?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We respond to all inquiries within 24 hours during business days. For urgent matters requiring immediate attention, please call our office directly at +977-9802781400."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide on-site consultation and assessment?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our experienced engineers can visit your facility to assess your energy requirements and recommend customized solutions for optimal performance."
        }
      },
      {
        "@type": "Question",
        "name": "Which areas do you serve in Nepal?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We serve clients nationwide from our offices in Biratnagar (Head Office), Kathmandu, Birgunj, and Bhairahawa, providing comprehensive coverage across Nepal."
        }
      },
      {
        "@type": "Question",
        "name": "What information should I include in my inquiry?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Please provide details about your facility type, current energy systems, specific requirements, and any challenges you're facing. The more information you share, the better we can assist you."
        }
      }
    ]
  };

  const FloatingElements = () => (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute top-1/2 left-1/4 transform -translate-y-1/2">
        <div className="w-32 h-32 bg-red-500/10 rounded-full animate-pulse" />
      </div>
      <div className="absolute top-1/3 right-1/4 transform -translate-y-1/2">
        <div className="w-24 h-24 bg-red-500/20 rounded-full animate-bounce" />
      </div>
      <div className="absolute bottom-1/4 left-1/3 transform -translate-y-1/2">
        <div className="w-16 h-16 bg-red-500/15 rounded-full animate-pulse delay-300" />
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>Contact Gortherm Energy | Industrial Boiler Experts Nepal</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Contact Gortherm Energy for industrial boiler solutions in Nepal. Get expert consultation for steam systems, water treatment & energy services." />
        
        {/* Keywords */}
        <meta name="keywords" content="contact Gortherm Energy, industrial boiler Nepal, boiler consultation, steam systems Nepal, energy solutions, Biratnagar contact, boiler service inquiry" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/contact" />
        
        {/* Language */}
        <html lang="en" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Contact Gortherm Energy | Get Industrial Energy Solutions" />
        <meta property="og:description" content="Reach out to Gortherm Energy for expert industrial boiler solutions, steam systems, and energy services across Nepal." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/contact" />
        <meta property="og:site_name" content="Gortherm Energy" />
        <meta property="og:locale" content="en_NP" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Gortherm Energy | Industrial Boiler Solutions" />
        <meta name="twitter:description" content="Get in touch for industrial energy solutions in Nepal." />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-snippet:-1" />
        
        {/* Geographic tags */}
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Biratnagar, Nepal" />
        <meta name="geo.position" content="26.4602126;87.2784241" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(contactSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema, null, 2)}
        </script>
      </Helmet>

      <div className="pt-16 bg-gradient-to-b from-red-50 to-white">
        {/* Hero Section */}
        <header className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 md:py-20 overflow-hidden">
          <FloatingElements />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center justify-center space-x-4 bg-white/10 px-6 py-3 rounded-full backdrop-blur-sm">
                <Zap className="w-5 h-5 md:w-6 md:h-6 text-red-400 animate-pulse" />
                <span className="text-white font-semibold text-sm md:text-base">Gortherm Energy</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center tracking-tight px-4">
                Contact Us for Industrial Energy Solutions
              </h1>
              <h2 className="text-xl md:text-2xl font-semibold text-red-300 text-center">
                Get Expert Consultation Today
              </h2>
              <p className="text-gray-300 text-center text-base md:text-lg max-w-2xl mx-auto leading-relaxed px-4">
                Have questions about industrial boiler solutions, steam systems, or energy services? Our expert team in Biratnagar is ready to help you find the perfect solution for your business needs.
              </p>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li><a href="/" className="hover:text-red-600">Home</a></li>
            <li>/</li>
            <li aria-current="page" className="text-gray-900 font-medium">Contact</li>
          </ol>
        </nav>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 md:-mt-12 relative z-10 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            
            {/* Contact Information Sidebar */}
            <aside className="lg:col-span-1 space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-8 backdrop-blur-lg border border-gray-100">
                <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6 flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                  <span>Contact Information</span>
                </h2>
                
                <div className="space-y-6">
                  {[
                    { 
                      Icon: MapPin, 
                      title: 'Our Address', 
                      content: 'Yashoda Bhawan, H08, Biratnagar 56613, Nepal',
                      schema: 'address'
                    },
                    { 
                      Icon: Phone, 
                      title: 'Phone Number', 
                      content: '+977-9802781400',
                      link: 'tel:+9779802781400',
                      schema: 'telephone'
                    },
                    { 
                      Icon: Mail, 
                      title: 'Email Address', 
                      content: 'info@gorthermenergy.com',
                      link: 'mailto:info@gorthermenergy.com',
                      schema: 'email'
                    },
                    { 
                      Icon: Clock, 
                      title: 'Business Hours', 
                      content: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 9:00 AM - 1:00 PM', 'Sunday: Closed'],
                      schema: 'openingHours'
                    }
                  ].map(({ Icon, title, content, link, schema }) => (
                    <article key={title} className="group hover:bg-gray-50 p-4 rounded-xl transition-all duration-300">
                      <div className="flex items-start space-x-4">
                        <div className="bg-red-100 p-3 rounded-lg group-hover:bg-red-200 transition-colors duration-300 flex-shrink-0">
                          <Icon className="w-5 h-5 md:w-6 md:h-6 text-red-500 group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 mb-1 text-sm md:text-base">{title}</h3>
                          {Array.isArray(content) ? (
                            content.map(line => (
                              <p key={line} className="text-gray-600 text-sm md:text-base break-words">{line}</p>
                            ))
                          ) : link ? (
                            <a href={link} className="text-gray-600 hover:text-red-600 text-sm md:text-base break-words">
                              {content}
                            </a>
                          ) : (
                            <p className="text-gray-600 text-sm md:text-base break-words">{content}</p>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Additional Info */}
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm md:text-base">Quick Response Guarantee</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    We respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly.
                  </p>
                </div>
              </div>
            </aside>

            {/* Contact Form */}
            <section className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 backdrop-blur-lg border border-gray-100">
                <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6 md:mb-8 flex items-center space-x-2">
                  <Send className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                  <span>Send Us a Message</span>
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {[
                      { name: 'name', label: 'Full Name', type: 'text', placeholder: 'Enter your full name' },
                      { name: 'email', label: 'Email Address', type: 'email', placeholder: 'your@email.com' },
                      { name: 'subject', label: 'Subject', type: 'text', placeholder: 'How can we help you?' }
                    ].map(({ name, label, type, placeholder }) => (
                      <div key={name} className="group md:col-span-1">
                        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-2">
                          {label} <span className="text-red-500">*</span>
                        </label>
                        <input
                          id={name}
                          type={type}
                          name={name}
                          value={formData[name]}
                          onChange={handleChange}
                          placeholder={placeholder}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 hover:border-red-300 text-sm md:text-base"
                          required
                          aria-required="true"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Phone Number with Country Selector */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative sm:w-48">
                          <button
                            type="button"
                            onClick={() => setIsOpen(!isOpen)}
                            className="w-full flex items-center justify-between space-x-2 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm md:text-base"
                            aria-label="Select country code"
                            aria-expanded={isOpen}
                          >
                            <div className="flex items-center space-x-2">
                              <span className="text-xl">{formData.phone.country.flag}</span>
                              <span className="text-gray-600">{formData.phone.country.dialCode}</span>
                            </div>
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          </button>

                          {isOpen && (
                            <div className="absolute z-20 mt-1 w-full sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96">
                              <div className="p-2 sticky top-0 bg-white">
                                <input
                                  type="text"
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  placeholder="Search countries..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                                  aria-label="Search countries"
                                />
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {filteredCountries.map((country) => (
                                  <button
                                    key={country.code}
                                    type="button"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        phone: { ...formData.phone, country }
                                      });
                                      setIsOpen(false);
                                      setSearchQuery('');
                                    }}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-3 text-sm"
                                  >
                                    <span className="text-xl">{country.flag}</span>
                                    <span className="text-gray-600 flex-1">{country.name}</span>
                                    <span className="text-gray-400 text-sm">{country.dialCode}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <input
                          id="phone"
                          type="tel"
                          value={formData.phone.number}
                          onChange={handlePhoneChange}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm md:text-base"
                          placeholder="Phone number"
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message Textarea */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Tell us about your requirements..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 hover:border-red-300 resize-none text-sm md:text-base"
                      required
                      aria-required="true"
                    />
                  </div>

                  {/* Submit Status Message */}
                  {submitStatus && (
                    <div className={`p-4 rounded-lg ${
                      submitStatus.includes('success') 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : submitStatus.includes('Failed')
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-blue-50 text-blue-800 border border-blue-200'
                    }`}>
                      <p className="text-sm md:text-base">{submitStatus}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitStatus === 'Sending...'}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 rounded-lg text-sm md:text-base font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-300 space-x-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Send message"
                  >
                    <span>{submitStatus === 'Sending...' ? 'Sending...' : 'Send Message'}</span>
                    <Send className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </form>
              </div>
            </section>
          </div>

          {/* Map Section */}
          <section className="mt-12 md:mt-16">
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 backdrop-blur-lg border border-gray-100">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center space-x-2">
                <MapPin className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                <span>Find Us on Map</span>
              </h2>
              <p className="text-gray-600 mb-6 text-sm md:text-base">
                Visit our office in Biratnagar for in-person consultations and demonstrations of our industrial energy solutions.
              </p>
              <div className="w-full h-64 sm:h-80 md:h-96 bg-gray-100 rounded-lg overflow-hidden relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.619586610787!2d87.2784241!3d26.4602126!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ef7522296704a3%3A0x85c7e51d4c846833!2sGortherm%20Energy%20p.ltd!5e1!3m2!1sen!2snp!4v1734191945729!5m2!1sen!2snp&output=embed"
                  title="Gortherm Energy Location Map - Biratnagar, Nepal"
                  className="w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  aria-label="Google Maps showing Gortherm Energy office location in Biratnagar"
                ></iframe>
              </div>
            </div>
          </section>

          {/* Additional SEO Content Section */}
          <section className="mt-12 md:mt-16 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8 text-gray-900">
              Why Contact Gortherm Energy?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 text-gray-700">
              <article className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Expert Consultation</h3>
                <p className="text-sm md:text-base leading-relaxed">
                  Get professional advice from our experienced team on industrial boiler solutions, steam systems, and energy optimization for your facility.
                </p>
              </article>
              
              <article className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Custom Solutions</h3>
                <p className="text-sm md:text-base leading-relaxed">
                  We design tailored industrial energy solutions based on your specific requirements, facility size, and operational needs.
                </p>
              </article>
              
              <article className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Quick Response</h3>
                <p className="text-sm md:text-base leading-relaxed">
                  Our team responds to all inquiries within 24 hours. For urgent matters, call us directly for immediate assistance.
                </p>
              </article>
              
              <article className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Site Visits Available</h3>
                <p className="text-sm md:text-base leading-relaxed">
                  Schedule an on-site assessment where our engineers evaluate your facility and recommend optimal energy solutions.
                </p>
              </article>
              
              <article className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Comprehensive Support</h3>
                <p className="text-sm md:text-base leading-relaxed">
                  From initial consultation to installation and ongoing maintenance, we provide complete support throughout your project lifecycle.
                </p>
              </article>
              
              <article className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Nationwide Service</h3>
                <p className="text-sm md:text-base leading-relaxed">
                  With branches in Biratnagar, Kathmandu, Birgunj, and Bhairahawa, we serve clients across Nepal with local expertise.
                </p>
              </article>
            </div>
          </section>

          {/* FAQ Section for SEO */}
          <section className="mt-12 md:mt-16 bg-white rounded-2xl p-6 md:p-8 shadow-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-gray-900">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6 max-w-4xl mx-auto">
              <article className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">
                  What services does Gortherm Energy provide?
                </h3>
                <p className="text-gray-700 text-sm md:text-base">
                  We specialize in industrial boiler sales and service, steam accessories supply, process engineering, and water treatment solutions for industries across Nepal.
                </p>
              </article>
              
              <article className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">
                  How quickly can I expect a response to my inquiry?
                </h3>
                <p className="text-gray-700 text-sm md:text-base">
                  We respond to all inquiries within 24 hours during business days. For urgent matters requiring immediate attention, please call our office directly at +977-9802781400.
                </p>
              </article>
              
              <article className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">
                  Do you provide on-site consultation and assessment?
                </h3>
                <p className="text-gray-700 text-sm md:text-base">
                  Yes, our experienced engineers can visit your facility to assess your energy requirements and recommend customized solutions for optimal performance.
                </p>
              </article>
              
              <article className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">
                  Which areas do you serve in Nepal?
                </h3>
                <p className="text-gray-700 text-sm md:text-base">
                  We serve clients nationwide from our offices in Biratnagar (Head Office), Kathmandu, Birgunj, and Bhairahawa, providing comprehensive coverage across Nepal.
                </p>
              </article>
              
              <article className="pb-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">
                  What information should I include in my inquiry?
                </h3>
                <p className="text-gray-700 text-sm md:text-base">
                  Please provide details about your facility type, current energy systems, specific requirements, and any challenges you're facing. The more information you share, the better we can assist you.
                </p>
              </article>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ContactPage;